import { useCallback, useEffect, useState } from 'react'

import { Capacitor } from '@capacitor/core'
import { BiometricAuth, BiometryError, BiometryErrorType, BiometryType } from '@aparajita/capacitor-biometric-auth'

import { hasNativePlugin } from '@/utils/nativePlugins'

const isBiometricPluginMissing = () => Capacitor.isNativePlatform() && !hasNativePlugin('BiometricAuthNative')

export interface BiometricAvailability {
  available: boolean
  biometryType: BiometryType
}

export const useBiometric = () => {
  const [availability, setAvailability] = useState<BiometricAvailability>({
    available: false,
    biometryType: BiometryType.none,
  })
  const [isChecking, setIsChecking] = useState(true)

  const checkAvailability = useCallback(async (): Promise<BiometricAvailability> => {
    try {
      // Binário sem o plugin nativo: chamar o wrapper JS entra em recursão
      // infinita e congela o app, então nem tenta.
      if (isBiometricPluginMissing()) {
        const next: BiometricAvailability = {
          available: false,
          biometryType: BiometryType.none,
        }
        setAvailability(next)
        return next
      }

      const result = await BiometricAuth.checkBiometry()
      const next: BiometricAvailability = {
        available: result.isAvailable,
        biometryType: result.biometryType,
      }
      setAvailability(next)
      return next
    } catch {
      const next: BiometricAvailability = {
        available: false,
        biometryType: BiometryType.none,
      }
      setAvailability(next)
      return next
    } finally {
      setIsChecking(false)
    }
  }, [])

  const authenticate = useCallback(async (reason: string): Promise<void> => {
    if (isBiometricPluginMissing()) {
      throw new Error('Biometria indisponível. Atualize o aplicativo pela loja.')
    }

    await BiometricAuth.authenticate({
      reason,
      androidTitle: 'Autenticação biométrica',
      cancelTitle: 'Cancelar',
      allowDeviceCredential: false,
      iosFallbackTitle: '',
    })
  }, [])

  const isUserCancel = useCallback((error: unknown): boolean => {
    if (error instanceof BiometryError) {
      return (
        error.code === BiometryErrorType.userCancel ||
        error.code === BiometryErrorType.appCancel ||
        error.code === BiometryErrorType.systemCancel
      )
    }
    return false
  }, [])

  useEffect(() => {
    checkAvailability()
  }, [checkAvailability])

  return {
    ...availability,
    isChecking,
    checkAvailability,
    authenticate,
    isUserCancel,
  }
}
